import React, { memo } from "react";
import { clsx } from "clsx";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import { type MediaConfig, Media, MediaFormat } from "~components/shared/media";
import { type CuratedItem, getPublishedAt } from "~types/types";
import { formatHeadline } from "~utils/formatHeadline";

import styles from "./LineupContentAudio8Up.module.css";

export const MEDIA_PRIORITY: MediaFormat[] = [MediaFormat.audioImage];

const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 1,
    desktopMaxWidth: 600,
    showCaption: false,
    useImageLink: false,
};

interface SeriesCardProps {
    index: number;
    item: CuratedItem;
}

// The headline is often compromised of the title and the name of the show
// The title and the name of the show are separated by a colon and as a result,
// we need to remove the title from the headline as it is duplicate information
// given that the eyebrow already contains the title
const parseTitleFromHeadline = (headline: string) => {
    const colonIndex = headline.indexOf(":");

    if (colonIndex === -1) {
        return headline;
    }

    return headline.slice(colonIndex + 1).trim();
};

const displayTitleAndPublishedDate = (item: CuratedItem) => {
    const publishedAt = getPublishedAt(item);

    if (!publishedAt) {
        if (!item.moduleMeta?.title) {
            return null;
        }

        return item.moduleMeta.title;
    }

    const date = new Date(publishedAt);
    const formattedDate = date.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
    });

    const bulletChar = "\u2022";

    return item.moduleMeta?.title
        ? `${item.moduleMeta.title} ${bulletChar} ${formattedDate}`
        : formattedDate;
};

export const SeriesCard = memo(({ item, index }: SeriesCardProps) => (
    <div className={styles.item} data-component="audio-series-card">
        <Media config={MEDIA_CONFIG} priority={MEDIA_PRIORITY} source={item} />

        <a
            href={item.url}
            className={styles.cardLink}
            data-link-index={index + 1}
        >
            <div className={styles.textContent}>
                {displayTitleAndPublishedDate(item) && (
                    <span
                        className={clsx(
                            styles.eyebrow,
                            typographyStyles.articleLabel
                        )}
                    >
                        {displayTitleAndPublishedDate(item)}
                    </span>
                )}

                {formatHeadline(item.headline) && (
                    <h3
                        className={clsx(
                            styles.headline,
                            typographyStyles.articleHeadlineXs
                        )}
                    >
                        {parseTitleFromHeadline(formatHeadline(item.headline))}
                    </h3>
                )}
            </div>
        </a>
    </div>
));

SeriesCard.displayName = "SeriesCard";
